/**
 * AdminUserCreatePage
 * [UC-004] Create new user form with email uniqueness validation and password requirements
 */
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../core/api-client';
import toast from 'react-hot-toast';

interface UserForm {
  name: string;
  surname: string;
  email: string;
  password: string;
  role: string;
}

interface FormErrors {
  name?: string;
  surname?: string;
  email?: string;
  password?: string;
  role?: string;
}

export function AdminUserCreatePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Form state
  const [form, setForm] = useState<UserForm>({
    name: '',
    surname: '',
    email: '',
    password: '',
    role: ''
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);

  const roleOptions = ['User', 'Admin'];

  // Password validation computed properties
  const hasMinLength = useMemo(() => form.password.length >= 8, [form.password]);
  const hasUppercase = useMemo(() => /[A-Z]/.test(form.password), [form.password]);
  const hasLowercase = useMemo(() => /[a-z]/.test(form.password), [form.password]);
  const hasNumber = useMemo(() => /\d/.test(form.password), [form.password]);
  const hasSpecialChar = useMemo(() => /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\;'`~]/.test(form.password), [form.password]);

  const isPasswordValid = useMemo(
    () => hasMinLength && hasUppercase && hasLowercase && hasNumber && hasSpecialChar,
    [hasMinLength, hasUppercase, hasLowercase, hasNumber, hasSpecialChar]
  );

  const isFormValid = useMemo(
    () =>
      form.name.trim() !== '' &&
      form.surname.trim() !== '' &&
      form.email.trim() !== '' &&
      form.password.trim() !== '' &&
      form.role !== '' &&
      isPasswordValid &&
      !Object.keys(errors).some(key => errors[key as keyof FormErrors]),
    [form, isPasswordValid, errors]
  );

  // Check email uniqueness on blur
  const handleEmailBlur = async () => {
    if (!form.email.trim()) return;

    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailPattern.test(form.email)) {
      setErrors(prev => ({ ...prev, email: t('adminUserCreate.invalidEmail', 'Please enter a valid email address') }));
      return;
    }

    try {
      const response = await api.get(`/users/email-exists?email=${encodeURIComponent(form.email)}`) as { exists: boolean };
      if (response.exists) {
        setErrors(prev => ({ ...prev, email: t('adminUserCreate.emailExists', 'This email address is already in use') }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors.email;
          return newErrors;
        });
      }
    } catch (error) {
      console.error('Failed to check email existence:', error);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Required field validation
    if (!form.name.trim()) {
      newErrors.name = t('adminUserCreate.nameRequired', 'Name is required');
    }

    if (!form.surname.trim()) {
      newErrors.surname = t('adminUserCreate.surnameRequired', 'Surname is required');
    }

    if (!form.email.trim()) {
      newErrors.email = t('adminUserCreate.emailRequired', 'Email is required');
    } else if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(form.email)) {
      newErrors.email = t('adminUserCreate.invalidEmail', 'Please enter a valid email address');
    }

    if (!form.password.trim()) {
      newErrors.password = t('adminUserCreate.passwordRequired', 'Password is required');
    } else if (!isPasswordValid) {
      newErrors.password = t('adminUserCreate.passwordRequirements', 'Password does not meet strength requirements');
    }

    if (!form.role) {
      newErrors.role = t('adminUserCreate.roleRequired', 'Role selection is required');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await api.post('/users', form);

      // Show success message
      setShowSuccessMessage(true);
      toast.success(t('adminUserCreate.success', 'User account created successfully!'));

      // Reset form
      setForm({
        name: '',
        surname: '',
        email: '',
        password: '',
        role: ''
      });
      setErrors({});

      // Hide success message after 5 seconds
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 5000);
    } catch (error: any) {
      if (error.response) {
        const status = error.response.status;
        const detail = error.response.data?.detail || error.response.data?.message;

        switch (status) {
          case 400:
            // Validation errors
            if (detail) {
              toast.error(detail);
              // Map specific field errors if available
              if (detail.includes('password')) {
                setErrors(prev => ({ ...prev, password: detail }));
              }
            }
            break;
          case 403:
            toast.error(t('adminUserCreate.forbidden', 'You do not have permission to create users'));
            break;
          case 409:
            setErrors(prev => ({ ...prev, email: t('adminUserCreate.emailExists', 'Email already exists') }));
            break;
          case 500:
          case 502:
            toast.error(t('adminUserCreate.serverError', 'Server error. Please try again later.'));
            break;
          default:
            toast.error(t('adminUserCreate.unknownError', 'An unexpected error occurred'));
        }
      } else {
        toast.error(t('adminUserCreate.networkError', 'Network error. Please check your connection.'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/admin/dashboard');
  };

  const handleInputChange = (field: keyof UserForm, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <div className="max-w-full overflow-x-hidden">
      <div className="mb-5 sm:mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-[var(--text-primary)]">{t('adminUserCreate.title', 'Create User')}</h1>
        <p className="mt-1.5 text-base sm:text-sm text-[var(--text-secondary)]">{t('adminUserCreate.subtitle', 'Add a new user to the system with their details and role assignment')}</p>
      </div>

      {/* Success Message */}
      {showSuccessMessage && (
        <div className="mb-5 sm:mb-6 p-4 bg-green-600/15 border border-green-600/25 rounded-lg">
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
            <p className="text-base sm:text-sm font-medium text-green-700 dark:text-green-500">{t('adminUserCreate.successMessage', 'User account created successfully!')}</p>
          </div>
        </div>
      )}

      {/* Create User Form */}
      <div className="bg-[var(--surface-base)] rounded-lg border border-[var(--border-subtle)] p-5 sm:p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name Field */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">{t('adminUserCreate.nameLabel', 'Name')}</label>
            <div className="relative">
              <input
                value={form.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                type="text"
                id="name"
                className={`w-full px-4 py-3 sm:py-2.5 bg-[var(--surface-raised)] border rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:ring-1 text-base sm:text-sm ${
                  errors.name ? 'border-red-500/50 focus:border-red-400 focus:ring-red-400' : 'border-[var(--border-subtle)] focus:border-[var(--accent-primary)] focus:ring-[var(--accent-primary)]'
                }`}
                placeholder={t('adminUserCreate.namePlaceholder', 'Enter first name')}
              />
              {errors.name && (
                <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </div>
            {errors.name && <p className="mt-1.5 text-sm text-red-400">{errors.name}</p>}
          </div>

          {/* Surname Field */}
          <div>
            <label htmlFor="surname" className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">{t('adminUserCreate.surnameLabel', 'Surname')}</label>
            <div className="relative">
              <input
                value={form.surname}
                onChange={(e) => handleInputChange('surname', e.target.value)}
                type="text"
                id="surname"
                className={`w-full px-4 py-3 sm:py-2.5 bg-[var(--surface-raised)] border rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:ring-1 text-base sm:text-sm ${
                  errors.surname ? 'border-red-500/50 focus:border-red-400 focus:ring-red-400' : 'border-[var(--border-subtle)] focus:border-[var(--accent-primary)] focus:ring-[var(--accent-primary)]'
                }`}
                placeholder={t('adminUserCreate.surnamePlaceholder', 'Enter last name')}
              />
              {errors.surname && (
                <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </div>
            {errors.surname && <p className="mt-1.5 text-sm text-red-400">{errors.surname}</p>}
          </div>

          {/* Email Field */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">{t('adminUserCreate.emailLabel', 'Email')}</label>
            <div className="relative">
              <input
                value={form.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                onBlur={handleEmailBlur}
                type="email"
                id="email"
                className={`w-full px-4 py-3 sm:py-2.5 bg-[var(--surface-raised)] border rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:ring-1 text-base sm:text-sm ${
                  errors.email ? 'border-red-500/50 focus:border-red-400 focus:ring-red-400' : 'border-[var(--border-subtle)] focus:border-[var(--accent-primary)] focus:ring-[var(--accent-primary)]'
                }`}
                placeholder={t('adminUserCreate.emailPlaceholder', 'Enter email address')}
              />
              {errors.email && (
                <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </div>
            {errors.email && <p className="mt-1.5 text-sm text-red-400">{errors.email}</p>}
          </div>

          {/* Password Field */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">{t('adminUserCreate.passwordLabel', 'Password')}</label>
            <div className="relative">
              <input
                value={form.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                type={showPassword ? 'text' : 'password'}
                id="password"
                className={`w-full px-4 py-3 sm:py-2.5 pr-12 bg-[var(--surface-raised)] border rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)] focus:ring-1 text-base sm:text-sm ${
                  errors.password ? 'border-red-500/50 focus:border-red-400 focus:ring-red-400' : 'border-[var(--border-subtle)] focus:border-[var(--accent-primary)] focus:ring-[var(--accent-primary)]'
                }`}
                placeholder={t('adminUserCreate.passwordPlaceholder', 'Enter password')}
              />
              <button
                onClick={() => setShowPassword(!showPassword)}
                type="button"
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
              {errors.password && (
                <svg className="absolute right-12 top-1/2 transform -translate-y-1/2 w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              )}
            </div>
            {errors.password && <p className="mt-1.5 text-sm text-red-400">{errors.password}</p>}

            {/* Password Requirements */}
            <div className="mt-3 space-y-2">
              <p className="text-sm text-[var(--text-muted)]">{t('adminUserCreate.passwordRequirementsHeader', 'Password must contain:')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className={hasMinLength ? 'text-green-500' : 'text-[var(--text-muted)]'}>
                    {hasMinLength ? '✓' : '○'}
                  </span>
                  <span className={hasMinLength ? 'text-green-500' : 'text-[var(--text-muted)]'}>{t('adminUserCreate.reqMinLength', 'At least 8 characters')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={hasUppercase ? 'text-green-500' : 'text-[var(--text-muted)]'}>
                    {hasUppercase ? '✓' : '○'}
                  </span>
                  <span className={hasUppercase ? 'text-green-500' : 'text-[var(--text-muted)]'}>{t('adminUserCreate.reqUppercase', 'One uppercase letter')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={hasLowercase ? 'text-green-500' : 'text-[var(--text-muted)]'}>
                    {hasLowercase ? '✓' : '○'}
                  </span>
                  <span className={hasLowercase ? 'text-green-500' : 'text-[var(--text-muted)]'}>{t('adminUserCreate.reqLowercase', 'One lowercase letter')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={hasNumber ? 'text-green-500' : 'text-[var(--text-muted)]'}>
                    {hasNumber ? '✓' : '○'}
                  </span>
                  <span className={hasNumber ? 'text-green-500' : 'text-[var(--text-muted)]'}>{t('adminUserCreate.reqNumber', 'One number')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={hasSpecialChar ? 'text-green-500' : 'text-[var(--text-muted)]'}>
                    {hasSpecialChar ? '✓' : '○'}
                  </span>
                  <span className={hasSpecialChar ? 'text-green-500' : 'text-[var(--text-muted)]'}>{t('adminUserCreate.reqSpecialChar', 'One special character')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Role Selection */}
          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1.5">{t('adminUserCreate.roleLabel', 'Role')}</label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)}
                className={`relative w-full cursor-pointer rounded-lg bg-[var(--surface-raised)] py-3 sm:py-2.5 pl-4 pr-10 text-left ring-1 ring-inset text-base sm:text-sm ${
                  errors.role ? 'ring-red-500/50 text-red-400' : 'ring-[var(--border-subtle)] text-[var(--text-primary)]'
                }`}
              >
                <span className="block truncate">{form.role || t('adminUserCreate.selectRole', 'Select role')}</span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <svg className="h-5 w-5 text-[var(--text-muted)]" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                  </svg>
                </span>
              </button>
              {isRoleDropdownOpen && (
                <ul className="absolute z-50 top-full mt-1 max-h-60 w-full overflow-auto rounded-lg bg-[var(--surface-overlay)] py-1 shadow-xl ring-1 ring-[var(--border-subtle)] text-base sm:text-sm">
                  {roleOptions.map((role) => (
                    <li
                      key={role}
                      onClick={() => {
                        handleInputChange('role', role);
                        setIsRoleDropdownOpen(false);
                      }}
                      className="relative cursor-pointer select-none py-3 sm:py-2.5 pl-4 pr-9 text-[var(--text-secondary)] hover:bg-[var(--accent-primary)] hover:text-white"
                    >
                      <span className={form.role === role ? 'font-semibold' : 'font-normal'}>{role}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {errors.role && <p className="mt-1.5 text-sm text-red-400">{errors.role}</p>}
          </div>

          {/* Form Actions */}
          <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <button
              onClick={handleCancel}
              type="button"
              className="w-full sm:w-auto px-5 py-3 sm:py-2.5 bg-[var(--surface-raised)] text-[var(--text-primary)] rounded-lg hover:bg-[var(--surface-overlay)] border border-[var(--border-subtle)] text-base sm:text-sm font-medium transition-colors"
            >
              {t('adminUserCreate.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              disabled={!isFormValid || isSubmitting}
              className={`w-full sm:w-auto px-5 py-3 sm:py-2.5 rounded-lg text-base sm:text-sm font-medium transition-colors ${
                isFormValid && !isSubmitting
                  ? 'bg-[var(--accent-primary)] text-[var(--accent-text)] hover:bg-[var(--accent-secondary)]'
                  : 'bg-[var(--surface-overlay)] text-[var(--text-muted)] cursor-not-allowed'
              }`}
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-[var(--text-muted)] border-t-transparent rounded-full animate-spin"></div>
                  {t('adminUserCreate.creating', 'Creating...')}
                </span>
              ) : (
                t('adminUserCreate.createUser', 'Create User')
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminUserCreatePage;
